import { useState, useEffect, useCallback, useContext } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { toast } from "sonner";
import {
  Folder,
  FolderPlus,
  FileText,
  ArrowLeft,
  Download,
  Calendar,
  MoreVertical,
  Trash2,
  Edit,
  Plus,
  X,
  MessageSquare,
  Share2,
  ExternalLink,
  Users,
  Send,
  PenTool,
  Clock,
  User,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  Bell,
  Lock,
  Shield,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "../ui/resizable";
import { UserModulesContext } from "../../../../components/UserModuleContext/index";
import {
  fetchDocuments,
  uploadDocument,
  downloadDocument,
  downloadDocumentBlob,
  fetchChairBrief,
  downloadChairBriefPdf,
  fetchDocumentNotes,
  createDocumentNote,
  deleteDocumentNote,
  shareDocument,
  fetchDocumentSignatures,
  remindDocumentSignatures,
} from "../../services/businessDashApi";
import { getAccountInfo } from "../../../../apis/User";
import { cn } from "../../lib/utils";

const folderColors = [
  { name: "Blue", value: "bg-pastel-blue" },
  { name: "Green", value: "bg-pastel-green" },
  { name: "Purple", value: "bg-pastel-purple" },
  { name: "Yellow", value: "bg-pastel-yellow" },
  { name: "Pink", value: "bg-pink-100" },
  { name: "Orange", value: "bg-orange-100" },
  { name: "Slate", value: "bg-slate-100" },
];

/** Built-in folders (ids stable for URL / localStorage). */
const BUILTIN_FOLDERS = [
  { id: "1", name: "Contracts", color: "bg-pastel-blue", icon: "folder", parentId: undefined },
  { id: "2", name: "Policies", color: "bg-pastel-green", icon: "folder", parentId: undefined },
  { id: "3", name: "Leases", color: "bg-pastel-purple", icon: "folder", parentId: undefined },
  { id: "4", name: "Meeting Minutes", color: "bg-pastel-yellow", icon: "folder", parentId: undefined },
  { id: "7", name: "Strategy", color: "bg-orange-100", icon: "folder", parentId: undefined },
  { id: "5", name: "Governance", color: "bg-slate-100", icon: "folder", parentId: undefined },
  {
    id: "6",
    name: "Chair",
    color: "bg-primary/10",
    icon: "folder",
    parentId: "5",
    restricted: true,
    allowedRoles: [
      "chairperson",
      "president",
      "vice-chair",
      "vice-president",
      "secretary",
      "admin",
    ],
  },
];

/** Matches backend `user_may_access_chair_folder` / Chair folder UI visibility */
const CHAIR_FOLDER_ROLES = [
  "chairperson",
  "president",
  "vice-chair",
  "vice-president",
  "secretary",
  "admin",
];

const FOLDER_ID_TO_SLUG = {
  "1": "contracts",
  "2": "policies",
  "3": "leases",
  "4": "meeting_minutes",
  "5": "governance",
  "6": "chair",
  "7": "strategy",
};

const CUSTOM_FOLDERS_KEY = "azilis_doc_custom_folders_v1";

function loadJsonStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const p = JSON.parse(raw);
    return p ?? fallback;
  } catch {
    return fallback;
  }
}

function saveJsonStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function fileExt(name) {
  if (!name || typeof name !== "string") return "";
  const p = name.split(".").pop();
  return p ? p.toUpperCase() : "FILE";
}

function normalizeApiDocument(doc) {
  const id = String(doc.id ?? doc.uuid ?? "");
  const name = doc.name || doc.file_name || "Document";
  const folderSlug = doc.board_folder ?? doc.folder_slug ?? doc.folder ?? "";
  return {
    id,
    name,
    type: fileExt(name),
    uploadDate: (doc.upload_date || doc.uploadDate || "").slice(0, 10) || "—",
    size: doc.formatted_size || doc.size || "—",
    board_folder: folderSlug,
    raw: doc,
    isVirtual: false,
    isChairBriefPdf: false,
    content: "",
    notes: [],
    signatures: [],
  };
}

function slugToFolderId(slugRaw) {
  const slug = String(slugRaw || "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .trim();
  if (!slug) return null;
  const hit = Object.entries(FOLDER_ID_TO_SLUG).find(([, s]) => s === slug);
  return hit ? hit[0] : null;
}

function inferFolderId(doc) {
  const fromApi = slugToFolderId(doc.board_folder || doc.folder_slug || doc.folder);
  if (fromApi) return fromApi;
  const t = `${doc.document_type || doc.type || ""} ${doc.name || ""}`.toLowerCase();
  if (t.includes("contract")) return "1";
  if (t.includes("policy") || t.includes("privacy") || t.includes("handbook")) return "2";
  if (t.includes("lease")) return "3";
  if (t.includes("strategy") || t.includes("supplier")) return "7";
  if (t.includes("governance") && !t.includes("chair")) return "5";
  if (t.includes("chair")) return "6";
  return "4";
}

function canViewChairFolder(boardMemberRole, isAccessManager) {
  if (isAccessManager) return true;
  const ur = normalizeUserRole(boardMemberRole, false);
  return CHAIR_FOLDER_ROLES.includes(ur);
}

function normalizeUserRole(boardMemberRole, isAccessManager) {
  if (isAccessManager) return "admin";
  const r = (boardMemberRole || "").toLowerCase().replace(/\s+/g, "-");
  if (r === "president") return "president";
  if (r === "vice-president" || r === "vice_president") return "vice-president";
  if (r.includes("vice") && r.includes("chair")) return "vice-chair";
  if (r.includes("chair") && !r.includes("vice")) return "chairperson";
  if (r === "secretary") return "secretary";
  if (r === "employee") return "employee";
  if (r === "admin" || r === "administrator") return "admin";
  return "ordinary";
}

const canAccessFolder = (folder, userRole) => {
  if (!folder.restricted) return true;
  if (!folder.allowedRoles?.length) return true;
  return folder.allowedRoles.includes(userRole);
};

const isGovernanceFolder = (folder) =>
  folder.id === "5" || folder.id === "6" || folder.parentId === "5";

function filterFoldersByDashboard(folders, dashboardContext) {
  if (dashboardContext === "business") return folders;
  return folders.filter((f) => !isGovernanceFolder(f));
}

function formatNoteCreated(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d
      .toLocaleString("sv-SE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", "");
  } catch {
    return String(iso);
  }
}

function mapNoteFromApi(n) {
  if (!n || typeof n !== "object") return null;
  return {
    id: String(n.id),
    author: n.author ?? "Member",
    content: n.content ?? "",
    createdAt: formatNoteCreated(n.created_at),
  };
}

/** Map GET …/signatures/ payload to viewer card shape */
function mapSignersPayloadToLegacy(sigPayload) {
  const signers = sigPayload?.signers;
  if (!Array.isArray(signers) || signers.length === 0) return [];
  return signers.map((s, i) => ({
    id: String(s.id ?? `sig-${i}`),
    signerName: s.name ?? s.signer_name ?? s.signerName ?? "Signer",
    signerEmail: s.email ?? s.signer_email ?? "",
    signedAt: s.signed_at ?? s.signedAt ?? null,
    status:
      String(s.status || "pending").toLowerCase() === "signed"
        ? "signed"
        : String(s.status || "pending").toLowerCase() === "declined"
        ? "declined"
        : "pending",
  }));
}

/**
 * @param {{
 *   dashboardContext?: 'business' | 'admin' | 'user',
 *   navigateToDocumentId?: string | null,
 *   onDocumentNavigated?: () => void,
 *   strategyContext?: { source: string, sourceTitle: string } | null,
 *   onBackToStrategy?: () => void,
 *   onStorageLimitReached?: (info: unknown) => void,
 * }} props
 */
const DocumentFolders = ({
  dashboardContext = "business",
  navigateToDocumentId = null,
  onDocumentNavigated,
  strategyContext = null,
  onBackToStrategy,
  onStorageLimitReached,
}) => {
  const { boardMemberRole, isAccessManager } = useContext(UserModulesContext);
  const userRole = normalizeUserRole(boardMemberRole, isAccessManager);

  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isPenneoDialogOpen, setIsPenneoDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedColor, setSelectedColor] = useState(folderColors[0].value);
  const [editingFolder, setEditingFolder] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [shareEmail, setShareEmail] = useState("");
  const [shareType, setShareType] = useState("internal");
  const [loading, setLoading] = useState(true);
  const [currentUserName, setCurrentUserName] = useState("Member");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docYear] = useState(() => new Date().getFullYear());
  const [viewerNotes, setViewerNotes] = useState([]);
  const [signaturesPayload, setSignaturesPayload] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [sigsLoading, setSigsLoading] = useState(false);
  const [noteCounts, setNoteCounts] = useState({});
  const [signatureSummaryByDocId, setSignatureSummaryByDocId] = useState({});
  const [shareExpiresDays, setShareExpiresDays] = useState(7);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getAccountInfo();
        const data = res?.data?.data || res?.data || {};
        const name =
          [data.first_name, data.last_name].filter(Boolean).join(" ").trim() ||
          data.name ||
          data.email ||
          "Member";
        if (alive) setCurrentUserName(name);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const buildFolderTree = useCallback(
    (apiDocs, chairPayload) => {
      const custom = loadJsonStorage(CUSTOM_FOLDERS_KEY, []);
      const base = [...BUILTIN_FOLDERS, ...custom].map((f) => ({
        ...f,
        documents: [],
      }));

      const byId = Object.fromEntries(base.map((f) => [f.id, { ...f, documents: [] }]));

      const docs = Array.isArray(apiDocs) ? apiDocs : [];
      docs.forEach((d) => {
        const normalized = normalizeApiDocument(d);
        if (
          normalized.type === "dietary_requirements" ||
          d.document_type === "dietary_requirements"
        ) {
          return;
        }
        const fid = inferFolderId(d);
        if (byId[fid]) {
          byId[fid].documents.push(normalized);
        } else {
          byId["4"].documents.push(normalized);
        }
      });

      const brief = chairPayload?.data ?? chairPayload;
      if (brief && typeof brief === "object" && brief.visible === true) {
        const summary = brief.summary ?? brief.body ?? "";
        const highlights = Array.isArray(brief.highlights) ? brief.highlights : [];
        const hlText = highlights.length ? highlights.join("\n• ") : "";
        const text = [summary, hlText ? `Highlights:\n• ${hlText}` : ""]
          .filter(Boolean)
          .join("\n\n");
        const headline = brief.title ?? brief.headline ?? "Chair Brief";
        const virtual = {
          id: "__chair_brief_pdf__",
          name: `${headline}.pdf`,
          type: "PDF",
          uploadDate: new Date().toISOString().slice(0, 10),
          size: "—",
          content: text || "Chair brief content is available. Use Download for the PDF.",
          isVirtual: true,
          isChairBriefPdf: true,
          chairBriefId: brief.brief_id ?? brief.id ?? null,
          notes: [],
          signatures: [],
        };
        if (byId["6"]) {
          byId["6"].documents = [
            virtual,
            ...byId["6"].documents.filter((x) => x.id !== "__chair_brief_pdf__"),
          ];
        }
      }

      return Object.values(byId);
    },
    []
  );

  const refreshDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await fetchDocuments({ year: docYear });
      let chairPayload = null;
      if (canViewChairFolder(boardMemberRole, isAccessManager)) {
        try {
          chairPayload = await fetchChairBrief();
        } catch {
          chairPayload = null;
        }
      }
      const tree = buildFolderTree(docs, chairPayload);
      setFolders(tree);
      setNoteCounts({});
      setSignatureSummaryByDocId({});
    } catch (e) {
      console.error(e);
      toast.error("Failed to load documents");
      setFolders(buildFolderTree([], null));
    } finally {
      setLoading(false);
    }
  }, [docYear, buildFolderTree, boardMemberRole, isAccessManager]);

  useEffect(() => {
    refreshDocuments();
  }, [refreshDocuments]);

  useEffect(() => {
    if (!viewingDocument || viewingDocument.isVirtual || viewingDocument.isChairBriefPdf) {
      setViewerNotes([]);
      setSignaturesPayload(null);
      return;
    }
    let alive = true;
    (async () => {
      setNotesLoading(true);
      setSigsLoading(true);
      try {
        const [notesRaw, sigRaw] = await Promise.all([
          fetchDocumentNotes(viewingDocument.id),
          fetchDocumentSignatures(viewingDocument.id),
        ]);
        if (!alive) return;
        const notesList = Array.isArray(notesRaw)
          ? notesRaw.map(mapNoteFromApi).filter(Boolean)
          : [];
        setViewerNotes(notesList);
        setNoteCounts((prev) => ({ ...prev, [viewingDocument.id]: notesList.length }));
        setSignaturesPayload(sigRaw && typeof sigRaw === "object" ? sigRaw : null);
        const signers = sigRaw?.signers;
        if (Array.isArray(signers) && signers.length > 0) {
          const signed = signers.filter(
            (s) => String(s.status || "").toLowerCase() === "signed"
          ).length;
          setSignatureSummaryByDocId((prev) => ({
            ...prev,
            [viewingDocument.id]: { signed, total: signers.length },
          }));
        } else {
          setSignatureSummaryByDocId((prev) => {
            const next = { ...prev };
            delete next[viewingDocument.id];
            return next;
          });
        }
      } catch (e) {
        if (!alive) return;
        console.error(e);
        toast.error("Could not load notes or signatures for this document");
        setViewerNotes([]);
        setSignaturesPayload(null);
      } finally {
        if (alive) {
          setNotesLoading(false);
          setSigsLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [viewingDocument]);

  useEffect(() => {
    if (!navigateToDocumentId) return;
    for (const folder of folders) {
      const document = folder.documents.find((doc) => doc.id === navigateToDocumentId);
      if (document) {
        if (!canAccessFolder(folder, userRole)) return;
        setActiveFolder(folder);
        setViewingDocument(document);
        onDocumentNavigated?.();
        break;
      }
    }
  }, [navigateToDocumentId, folders, userRole, onDocumentNavigated]);

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const custom = loadJsonStorage(CUSTOM_FOLDERS_KEY, []);
    const newFolder = {
      id: `c-${Date.now()}`,
      name: newFolderName.trim(),
      color: selectedColor,
      icon: "folder",
      documents: [],
      isCustom: true,
    };
    const next = [...custom, newFolder];
    saveJsonStorage(CUSTOM_FOLDERS_KEY, next);
    setFolders((prev) => [...prev, { ...newFolder, documents: [] }]);
    setNewFolderName("");
    setSelectedColor(folderColors[0].value);
    setIsNewFolderOpen(false);
    toast.success("Folder created");
  };

  const handleDeleteFolder = (folderId) => {
    const f = folders.find((x) => x.id === folderId);
    if (!f?.isCustom) {
      toast.error("Built-in folders cannot be deleted.");
      return;
    }
    if (f.documents.length > 0) {
      toast.error("Remove or move documents before deleting this folder.");
      return;
    }
    const custom = loadJsonStorage(CUSTOM_FOLDERS_KEY, []).filter((x) => x.id !== folderId);
    saveJsonStorage(CUSTOM_FOLDERS_KEY, custom);
    setFolders((prev) => prev.filter((x) => x.id !== folderId));
    if (activeFolder?.id === folderId) setActiveFolder(null);
    toast.success("Folder removed");
  };

  const handleEditFolder = () => {
    if (!editingFolder || !newFolderName.trim()) return;
    if (!editingFolder.isCustom) {
      toast.error("Built-in folders cannot be renamed here.");
      setEditingFolder(null);
      return;
    }
    const custom = loadJsonStorage(CUSTOM_FOLDERS_KEY, []).map((x) =>
      x.id === editingFolder.id ? { ...x, name: newFolderName.trim(), color: selectedColor } : x
    );
    saveJsonStorage(CUSTOM_FOLDERS_KEY, custom);
    setFolders((prev) =>
      prev.map((x) =>
        x.id === editingFolder.id ? { ...x, name: newFolderName.trim(), color: selectedColor } : x
      )
    );
    setEditingFolder(null);
    setNewFolderName("");
    setSelectedColor(folderColors[0].value);
    toast.success("Folder updated");
  };

  const handleAddNote = async () => {
    if (!viewingDocument || !newNoteContent.trim() || !activeFolder) return;
    if (viewingDocument.isVirtual || viewingDocument.isChairBriefPdf) return;
    try {
      const created = await createDocumentNote(viewingDocument.id, newNoteContent.trim());
      const mapped = mapNoteFromApi(created);
      if (mapped) {
        setViewerNotes((prev) => {
          const next = [...prev, mapped];
          return next;
        });
        setNoteCounts((prev) => ({
          ...prev,
          [viewingDocument.id]: (prev[viewingDocument.id] ?? 0) + 1,
        }));
      }
      setNewNoteContent("");
      toast.success("Note added");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!viewingDocument || !activeFolder) return;
    if (viewingDocument.isVirtual || viewingDocument.isChairBriefPdf) return;
    try {
      await deleteDocumentNote(viewingDocument.id, noteId);
      setViewerNotes((prev) => prev.filter((n) => n.id !== noteId));
      setNoteCounts((prev) => {
        const next = { ...prev };
        const cur = next[viewingDocument.id] ?? 0;
        next[viewingDocument.id] = Math.max(0, cur - 1);
        return next;
      });
      toast.success("Note removed");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not delete note");
    }
  };

  const handleShare = async () => {
    if (!shareEmail.trim() || !viewingDocument) return;
    if (viewingDocument.isVirtual || viewingDocument.isChairBriefPdf) {
      toast.info("Sharing applies to uploaded board documents.");
      return;
    }
    try {
      const payload = {
        email: shareEmail.trim(),
        scope: shareType === "internal" ? "internal" : "external",
        expires_in_days: Number.isFinite(Number(shareExpiresDays))
          ? Math.min(365, Math.max(1, Number(shareExpiresDays)))
          : 7,
      };
      const res = await shareDocument(viewingDocument.id, payload);
      const url = res?.share_url ?? res?.shareUrl;
      toast.success(
        url
          ? `Share created. Link: ${url}`
          : `Share recorded for ${shareEmail.trim()}`
      );
      setShareEmail("");
      setIsShareDialogOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not share document");
    }
  };

  const handleSendToPenneo = () => {
    if (signaturesPayload?.status === "not_configured") {
      toast.info(signaturesPayload?.message || "Penneo is not configured yet.");
      setIsPenneoDialogOpen(false);
      return;
    }
    toast.info("Use signature requests from your e-sign provider when enabled.");
    setIsPenneoDialogOpen(false);
  };

  const handleSendReminder = async (docId, e) => {
    e.stopPropagation();
    const summary = signatureSummaryByDocId[docId];
    if (summary && summary.total > 0 && summary.signed >= summary.total) {
      toast.info("No pending signatures to remind");
      return;
    }
    try {
      await remindDocumentSignatures(docId);
      toast.success("Reminder sent");
    } catch (e) {
      const st = e.response?.status;
      const detail = e.response?.data?.detail;
      if (st === 503) {
        toast.error(typeof detail === "string" ? detail : "Reminders are not available until e-sign is configured.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send reminder");
      }
    }
  };

  const getSignatureProgress = (doc) => {
    const cached = signatureSummaryByDocId[doc.id];
    if (cached && cached.total > 0) {
      return {
        signed: cached.signed,
        total: cached.total,
        allSigned: cached.signed === cached.total,
      };
    }
    const sigs = doc.signatures || [];
    if (sigs.length === 0) return null;
    const signed = sigs.filter((s) => s.status === "signed").length;
    const total = sigs.length;
    return { signed, total, allSigned: signed === total };
  };

  const handleDownloadViewer = async () => {
    if (!viewingDocument) return;
    if (viewingDocument.isChairBriefPdf) {
      try {
        await downloadChairBriefPdf({
          briefId: viewingDocument.chairBriefId ?? undefined,
        });
        toast.success("Download started");
      } catch (e) {
        toast.error(e.message || "Could not download chair brief PDF");
      }
      return;
    }
    try {
      const result = await downloadDocument(viewingDocument.id);
      if (result instanceof Blob) {
        const url = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", viewingDocument.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else if (result?.url) {
        window.open(result.url, "_blank", "noopener");
      } else {
        const blob = await downloadDocumentBlob(viewingDocument.id);
        if (blob instanceof Blob && blob.size > 0 && blob.type?.includes("json")) {
          const text = await blob.text();
          try {
            const j = JSON.parse(text);
            if (j?.url) {
              window.open(j.url, "_blank", "noopener");
              return;
            }
          } catch {
            /* fall through */
          }
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", viewingDocument.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !activeFolder) {
      toast.error("Choose a file");
      return;
    }
    const slug =
      FOLDER_ID_TO_SLUG[activeFolder.id] ||
      String(activeFolder.name || "general")
        .toLowerCase()
        .replace(/\s+/g, "_");
    setUploading(true);
    try {
      const month = new Date().getMonth() + 1;
      const meetingQuarter = Math.min(4, Math.max(1, Math.ceil(month / 3)));
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("name", uploadFile.name);
      formData.append("document_type", activeFolder.name);
      formData.append("meeting_quarter", meetingQuarter);
      formData.append("year", docYear);
      formData.append("confidentiality_level", "all");
      formData.append("board_folder", slug);

      await uploadDocument(formData);
      setIsUploadOpen(false);
      setUploadFile(null);
      toast.success("Document uploaded");
      await refreshDocuments();
    } catch (error) {
      console.error(error);
      if (error.response?.status === 413) {
        if (error.response?.data?.requires_upgrade) {
          onStorageLimitReached?.(error.response.data);
        } else {
          toast.error(error.response?.data?.error || "Storage limit reached");
        }
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.error || "Invalid file");
      } else if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Not allowed to upload to this folder");
      } else {
        toast.error("Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const openEditDialog = (folder) => {
    setEditingFolder(folder);
    setNewFolderName(folder.name);
    setSelectedColor(folder.color);
  };

  const renderDocumentViewer = () => {
    if (!viewingDocument) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <FileText className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No document selected</h3>
          <p className="text-sm text-muted-foreground/70">Select a document from a folder to view its contents</p>
        </div>
      );
    }

    const isVirtualDoc = viewingDocument.isVirtual || viewingDocument.isChairBriefPdf;
    const vd = isVirtualDoc
      ? viewingDocument
      : {
          ...viewingDocument,
          notes: viewerNotes,
          signatures: mapSignersPayloadToLegacy(signaturesPayload),
        };

    return (
      <div className="flex flex-col h-full min-h-0">
        {strategyContext && onBackToStrategy && (
          <button
            type="button"
            onClick={onBackToStrategy}
            className="w-full flex items-center gap-2 px-4 py-2.5 bg-muted/30 border-b border-border/30 text-sm text-foreground/80 hover:bg-muted/50 hover:text-foreground transition-colors text-left"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            <span>Back to Strategy: {strategyContext.sourceTitle}</span>
          </button>
        )}

        <div className="p-4 border-b border-muted/20 shrink-0">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-6 h-6 text-primary shrink-0" />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground truncate">{vd.name}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <Badge variant="secondary">{vd.type}</Badge>
                  <span className="text-sm text-muted-foreground">{vd.size}</span>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {vd.uploadDate}
                  </span>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" type="button" onClick={() => setViewingDocument(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-background border shadow-lg z-50">
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => {
                    setShareType("internal");
                    setIsShareDialogOpen(true);
                  }}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Share Internally
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => {
                    setShareType("external");
                    setIsShareDialogOpen(true);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Share Externally
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              type="button"
              onClick={() => setIsPenneoDialogOpen(true)}
            >
              <PenTool className="w-4 h-4" />
              Send to Penneo
            </Button>

            <Button variant="outline" size="sm" className="gap-2" type="button" onClick={handleDownloadViewer}>
              <Download className="w-4 h-4" />
              Download
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={65} minSize={40}>
              <ScrollArea className="h-full">
                <div className="p-6">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-foreground/90 leading-relaxed">
                    {vd.content ||
                      (vd.isVirtual
                        ? "Use Download to open the file. Preview text may be shown above when provided by the server."
                        : "No text preview for this file. Use Download to open it locally.")}
                  </pre>

                  {(sigsLoading ||
                    (vd.signatures || []).length > 0 ||
                    signaturesPayload?.message ||
                    signaturesPayload?.status) && (
                    <div className="mt-6 pt-6 border-t border-muted/20">
                      <div className="flex items-center gap-2 mb-4">
                        <PenTool className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold text-foreground">E-signatures</h3>
                        {sigsLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin ml-auto text-muted-foreground" />
                        ) : (vd.signatures || []).length > 0 ? (
                          <Badge variant="secondary" className="ml-auto">
                            {(vd.signatures || []).filter((s) => s.status === "signed").length}/
                            {(vd.signatures || []).length} signed
                          </Badge>
                        ) : signaturesPayload?.provider ? (
                          <Badge variant="outline" className="ml-auto text-xs">
                            {signaturesPayload.provider}
                            {signaturesPayload.status ? ` · ${signaturesPayload.status}` : ""}
                          </Badge>
                        ) : null}
                      </div>
                      {signaturesPayload?.message && (vd.signatures || []).length === 0 && (
                        <p className="text-sm text-muted-foreground mb-4">{signaturesPayload.message}</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(vd.signatures || []).map((sig) => (
                          <div
                            key={sig.id}
                            className={cn(
                              "p-3 rounded-lg border",
                              sig.status === "signed"
                                ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800"
                                : sig.status === "declined"
                                ? "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800"
                                : "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800"
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  {sig.status === "signed" ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                                  ) : sig.status === "declined" ? (
                                    <XCircle className="w-4 h-4 text-red-600 shrink-0" />
                                  ) : (
                                    <Loader2 className="w-4 h-4 text-yellow-600 animate-spin shrink-0" />
                                  )}
                                  <span className="font-medium text-sm text-foreground truncate">
                                    {sig.signerName}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 break-all">{sig.signerEmail}</p>
                              </div>
                              <Badge
                                variant="secondary"
                                className={cn(
                                  "text-xs shrink-0",
                                  sig.status === "signed"
                                    ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                    : sig.status === "declined"
                                    ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                                    : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300"
                                )}
                              >
                                {sig.status === "signed"
                                  ? "Signed"
                                  : sig.status === "declined"
                                  ? "Declined"
                                  : "Pending"}
                              </Badge>
                            </div>
                            {sig.signedAt && (
                              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Signed: {sig.signedAt}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={35} minSize={25}>
              <div className="flex flex-col h-full border-l border-muted/20 min-h-0">
                <div className="p-4 border-b border-muted/20 shrink-0">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Notes</h3>
                    {notesLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin ml-auto text-muted-foreground" />
                    ) : (
                      <Badge variant="secondary" className="ml-auto">
                        {(vd.notes || []).length}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isVirtualDoc
                      ? "Notes apply to uploaded board documents."
                      : "Synced with the board document service."}
                  </p>
                </div>

                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-4">
                    {notesLoading ? (
                      <div className="flex justify-center py-8 text-muted-foreground">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : (vd.notes || []).length === 0 ? (
                      <div className="text-center py-8">
                        <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No notes yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(vd.notes || []).map((note) => (
                          <div key={note.id} className="p-3 bg-background rounded-lg border border-muted/20">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 mb-2 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  <User className="w-3 h-3 text-primary" />
                                </div>
                                <span className="text-sm font-medium text-foreground truncate">
                                  {note.author}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 hover:bg-destructive/10 shrink-0"
                                type="button"
                                onClick={() => handleDeleteNote(note.id)}
                              >
                                <Trash2 className="w-3 h-3 text-destructive" />
                              </Button>
                            </div>
                            <p className="text-sm text-foreground/80 mb-2 break-words">{note.content}</p>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {note.createdAt}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ScrollArea>

                <div className="p-4 border-t border-muted/20 shrink-0">
                  <Textarea
                    placeholder={isVirtualDoc ? "Notes are not available for this item" : "Add a note..."}
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    className="min-h-[80px] resize-none mb-3"
                    disabled={isVirtualDoc}
                  />
                  <Button
                    className="w-full gap-2"
                    type="button"
                    onClick={handleAddNote}
                    disabled={!newNoteContent.trim() || isVirtualDoc}
                  >
                    <Send className="w-4 h-4" />
                    Add Note as {currentUserName}
                  </Button>
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>

        <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
          <DialogContent className="bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {shareType === "internal" ? <Users className="w-5 h-5" /> : <ExternalLink className="w-5 h-5" />}
                Share {shareType === "internal" ? "Internally" : "Externally"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="shareEmail">Email Address</Label>
                <Input
                  id="shareEmail"
                  type="email"
                  placeholder={shareType === "internal" ? "colleague@company.com" : "external@partner.com"}
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shareExpires">Expires in (days)</Label>
                <Input
                  id="shareExpires"
                  type="number"
                  min={1}
                  max={365}
                  value={shareExpiresDays}
                  onChange={(e) => setShareExpiresDays(e.target.value)}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {shareType === "internal"
                  ? "The recipient receives access according to your organisation’s rules."
                  : "An external share record is created; full redeem flow may follow in a later release."}
              </p>
              <Button className="w-full" type="button" onClick={handleShare} disabled={!shareEmail.trim()}>
                Share Document
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isPenneoDialogOpen} onOpenChange={setIsPenneoDialogOpen}>
          <DialogContent className="bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PenTool className="w-5 h-5 text-primary" />
                E-sign (Penneo)
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm font-medium text-foreground mb-1">{vd.name}</p>
                <p className="text-xs text-muted-foreground">
                  {signaturesPayload?.status === "not_configured"
                    ? signaturesPayload?.message ||
                      "Electronic signing is not configured on the server yet."
                    : "Signer workflow is managed by your e-sign provider when integrated."}
                </p>
              </div>
              <Button className="w-full gap-2" type="button" onClick={handleSendToPenneo}>
                <PenTool className="w-4 h-4" />
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const renderFolderList = () => {
    const visible = filterFoldersByDashboard(folders, dashboardContext)
      .filter((folder) => canAccessFolder(folder, userRole))
      .filter((folder) => !folder.parentId);

    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="p-4 border-b border-muted/20 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-foreground">Documents</h2>
            <Dialog
              open={isNewFolderOpen || !!editingFolder}
              onOpenChange={(openDialog) => {
                if (!openDialog) {
                  setIsNewFolderOpen(false);
                  setEditingFolder(null);
                  setNewFolderName("");
                  setSelectedColor(folderColors[0].value);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  type="button"
                  onClick={() => setIsNewFolderOpen(true)}
                >
                  <FolderPlus className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-background">
                <DialogHeader>
                  <DialogTitle>{editingFolder ? "Edit Folder" : "Create New Folder"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="folderName">Folder Name</Label>
                    <Input
                      id="folderName"
                      placeholder="e.g., Contracts, Policies, Reports..."
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Folder Color</Label>
                    <div className="flex gap-2 flex-wrap">
                      {folderColors.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          className={cn(
                            "w-10 h-10 rounded-lg border-2 transition-all",
                            color.value,
                            selectedColor === color.value
                              ? "border-primary ring-2 ring-primary/20"
                              : "border-transparent hover:border-muted-foreground/30"
                          )}
                          onClick={() => setSelectedColor(color.value)}
                        />
                      ))}
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    type="button"
                    onClick={editingFolder ? handleEditFolder : handleCreateFolder}
                    disabled={!newFolderName.trim()}
                  >
                    {editingFolder ? "Save Changes" : "Create Folder"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-sm text-muted-foreground">Organize your board documents</p>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-2">
            {loading && (
              <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading…
              </div>
            )}
            {!loading &&
              visible.map((folder) => {
                const childFolders = filterFoldersByDashboard(folders, dashboardContext).filter(
                  (f) => f.parentId === folder.id && canAccessFolder(f, userRole)
                );

                return (
                  <div key={folder.id}>
                    <div
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                        activeFolder?.id === folder.id ? "bg-primary/10" : "hover:bg-muted/50"
                      )}
                      onClick={() => {
                        setActiveFolder(folder);
                        setViewingDocument(null);
                      }}
                    >
                      <div
                        className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                          folder.color
                        )}
                      >
                        <Folder className="w-4 h-4 text-foreground/70" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{folder.name}</p>
                        <p className="text-xs text-muted-foreground">{folder.documents.length} files</p>
                      </div>
                      {folder.isCustom ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" type="button">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-background border shadow-lg z-50">
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditDialog(folder);
                              }}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFolder(folder.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="w-8 shrink-0" />
                      )}
                    </div>

                    {childFolders.length > 0 && (
                      <div className="ml-4 pl-2 border-l border-muted/30">
                        {childFolders.map((childFolder) => (
                          <div
                            key={childFolder.id}
                            className={cn(
                              "flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors",
                              activeFolder?.id === childFolder.id ? "bg-primary/10" : "hover:bg-muted/50"
                            )}
                            onClick={() => {
                              setActiveFolder(childFolder);
                              setViewingDocument(null);
                            }}
                          >
                            <div
                              className={cn(
                                "w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0",
                                childFolder.color
                              )}
                            >
                              {childFolder.restricted ? (
                                <Shield className="w-3.5 h-3.5 text-primary" />
                              ) : (
                                <Folder className="w-3.5 h-3.5 text-foreground/70" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-foreground text-sm truncate">{childFolder.name}</p>
                                {childFolder.restricted && <Lock className="w-3 h-3 text-muted-foreground shrink-0" />}
                              </div>
                              <p className="text-xs text-muted-foreground">{childFolder.documents.length} files</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeFolder?.id === folder.id && (
                      <div className="ml-4 pl-4 border-l border-muted/30">
                        {folder.documents.length === 0 ? (
                          <div className="py-3 px-2 text-sm text-muted-foreground">No documents</div>
                        ) : (
                          folder.documents.map((doc) => {
                            const d = doc;
                            const sigProgress = getSignatureProgress(d);
                            const nCount = noteCounts[d.id] ?? 0;
                            return (
                              <div
                                key={doc.id}
                                className={cn(
                                  "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors",
                                  viewingDocument?.id === doc.id ? "bg-primary/10" : "hover:bg-muted/50"
                                )}
                                onClick={() => setViewingDocument(d)}
                              >
                                <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                                <span className="text-sm text-foreground truncate flex-1">{d.name}</span>
                                {nCount > 0 && (
                                  <Badge variant="secondary" className="text-xs flex-shrink-0">
                                    {nCount}
                                  </Badge>
                                )}
                                {sigProgress && (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <Badge
                                      variant="secondary"
                                      className={cn(
                                        "text-xs",
                                        sigProgress.allSigned
                                          ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                          : "bg-destructive text-destructive-foreground"
                                      )}
                                    >
                                      {sigProgress.signed}/{sigProgress.total}
                                    </Badge>
                                    {!sigProgress.allSigned && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-6 w-6 p-0 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
                                              type="button"
                                              onClick={(e) => handleSendReminder(doc.id, e)}
                                            >
                                              <Bell className="w-3.5 h-3.5 text-yellow-600" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Send reminder to {sigProgress.total - sigProgress.signed} pending signer(s)</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                )}
                                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              </div>
                            );
                          })
                        )}

                        <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start gap-2 mt-2 text-muted-foreground"
                              type="button"
                            >
                              <Plus className="w-4 h-4" />
                              Upload Document
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-background">
                            <DialogHeader>
                              <DialogTitle>Upload Document to {activeFolder?.name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-2">
                              <Input
                                type="file"
                                accept=".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                              />
                              <p className="text-xs text-muted-foreground">
                                Max 50 MB. Types: pdf, docx, xlsx, pptx, png, jpg.
                              </p>
                              <Button type="button" disabled={uploading || !uploadFile} onClick={handleUpload}>
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}
                  </div>
                );
              })}

            <Button
              variant="ghost"
              className="w-full justify-start gap-2 mt-2 text-muted-foreground"
              type="button"
              onClick={() => setIsNewFolderOpen(true)}
            >
              <Plus className="w-4 h-4" />
              Add Folder
            </Button>
          </div>
        </ScrollArea>
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-200px)] min-h-[600px]">
      <Card className="h-full border-0 shadow-soft rounded-soft bg-card/50 backdrop-blur-sm overflow-hidden">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          <ResizablePanel defaultSize={25} minSize={20} maxSize={40}>
            {renderFolderList()}
          </ResizablePanel>

          <ResizableHandle withHandle />

          <ResizablePanel defaultSize={75} minSize={50}>
            {renderDocumentViewer()}
          </ResizablePanel>
        </ResizablePanelGroup>
      </Card>
    </div>
  );
};

export default DocumentFolders;
